import React, { useState } from 'react';
import { Box, Typography, Button, List, ListItem, Menu, Collapse, CircularProgress } from '@mui/material';
import { TodoItem } from './DashboardTodoTypes';
import { styles } from './DashboardTodoConstants';

interface TodoMenuProps {
  item: TodoItem;
  anchorEl: HTMLElement | null;
  isOpen: boolean;
  isCompleted: boolean;
  isNoActionNeeded?: boolean;
  menuWidth?: number;
  onClose: () => void;
  onConfirm?: () => void;
  onTriggerOptout?: () => void;
  onLater?: () => void;
  zIndex?: number;
}

export const TodoMenu = ({
  item,
  anchorEl,
  isOpen,
  isCompleted,
  isNoActionNeeded,
  menuWidth,
  onClose,
  onConfirm,
  onTriggerOptout,
  onLater,
  zIndex,
}: TodoMenuProps) => {
  const [loadingAction, setLoadingAction] = useState<'confirm' | 'optout' | 'later' | null>(null);


  const handleTriggerOptoutClick = async () => {
    if (!onTriggerOptout) return;
    setLoadingAction('optout');
    try {
      await onTriggerOptout();
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleConfirmClick = async () => {
    if (!onConfirm) return;
    setLoadingAction('confirm');
    try {
      await onConfirm();
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleLaterClick = async () => {
    if (!onLater) return;
    setLoadingAction('later');
    try {
      await onLater();
      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingAction(null);
    }
  };

  const isAnyLoading = loadingAction !== null;

  return (
    <Menu
      anchorEl={anchorEl}
      open={isOpen}
      onClose={onClose}
      sx={{ zIndex: zIndex || 1300 }}
      TransitionComponent={Collapse}
      transitionDuration={250}
      transformOrigin={{ horizontal: 'left', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      slotProps={{
        paper: {
          sx: {
            ...styles.menuPaper,
            width: menuWidth,
          },
        },
      }}
    >
      <Box sx={{ p: 4 }}>
        {isNoActionNeeded ? (
          <>           
          <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'row', md: 'column', lg: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'center', md: 'flex-start', lg: 'center' },
                gap: 1,
                mb: 2,
              }}
            >
              <Typography sx={styles.actionText}>No action needed:</Typography>
            </Box>
          <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: { xs: 'center' },
                gap: 2,
              }}
            >
              <Typography sx={{ fontSize: '14px', color: '#3A155A'}}>
                We did not find any of your information on this site. Your data may appear in the future and action may be required then.
              </Typography>
              <Button
                  variant="contained"
                  sx={{
                    ...styles.button,
                  }}
                  onClick={onClose}
                >
                  I Understand
              </Button>
            </Box>
            </>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'row', md: 'column', lg: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'center', md: 'flex-start', lg: 'center' },
                gap: 1,
                mb: 2,
              }}
            >
              <Typography sx={styles.actionText}>{item.actionTitle}</Typography>
            </Box>

            <List sx={{ p: 0, mb: 2, listStyleType: 'decimal', pl: 6 }}>
              {item.actionItems.map((actionText, index) => (
                <ListItem
                  key={`${item.id}-${index}`}
                  sx={{
                    display: 'list-item',
                    p: 0,
                    mb: 1,
                    fontSize: '14px',
                    color: '#3A155A',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Box>{actionText}</Box>
                    {index === 0 && item.titleActionText && !isCompleted && (
                      <Button
                        sx={{
                          ...styles.button,
                          mt: 1.5,
                          minWidth: '125px',
                        }}
                        onClick={handleTriggerOptoutClick}
                        disabled={isCompleted || isAnyLoading}
                      >
                        {loadingAction === 'optout' ? (
                          <CircularProgress size={20} sx={{ color: '#fff' }} />
                        ) : (
                          item.titleActionText
                        )}
                      </Button>
                    )}
                  </Box>
                </ListItem>
              ))}
            </List>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              {!isCompleted && (
                <Button
                  variant="contained"
                  sx={styles.laterButton}
                  disabled={isAnyLoading}
                  onClick={handleLaterClick}
                >
                  {loadingAction === 'later' ? (
                    <CircularProgress size={20} sx={{ color: '#3A155A' }} />
                  ) : (
                    "I'll do this later"
                  )}
                </Button>
              )}
              <Button
                variant="contained"
                sx={styles.button}
                disabled={isCompleted || isAnyLoading}
                onClick={handleConfirmClick}
              >
                {loadingAction === 'confirm' ? (
                  <CircularProgress size={20} sx={{ color: '#fff' }} />
                ) : isCompleted ? (
                  "You've done this"
                ) : (
                  item.buttonText
                )}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Menu>
  );
};
